/**
 * FRONT-OFFICE-OS-V2-BUILD-V1 -- the home global-search modal's PURE model
 * (founder rulings FO-3/FO-4/FO-5, 2026-08-01).
 *
 * FO-3: ONE search box (mobile / UMR / name) is the front-desk home's hero
 * and the new FRONT DOOR of the R-SEARCH-FIRST structural gate -- never its
 * replacement: the standalone register page keeps its origin-marker
 * redirect law byte-unchanged (PatientCreatePage / parseRegisterOrigin).
 *
 * FO-4: results land in a CENTERED MODAL -- candidate cards (the
 * shared-phone family list off the EXISTING patient-master search wire),
 * pick the person; nothing found renders the minimal copy
 * "No patients found" with exactly ONE action, "Register patient",
 * inside the same modal.
 *
 * FO-5 + C13 SEARCH FIRST: the in-modal register view opens ONLY behind
 * search evidence. In a modal there is no URL to mark, so the EVIDENCE
 * STATE MACHINE below is the structural gate (the in-modal twin of the
 * `from=` marker; same three tokens) and is pinned. It is a UI gate,
 * never the duplicate-security boundary -- THE ONE writer's deterministic
 * dedupe remains binding on every submit.
 *
 * D5 (as corrected by the PR #414 founder review -- DRAFT PRIVACY): the
 * register view's draft is SESSION-storage-bound and ACTOR-bound; a strict
 * parse discards a malformed or cross-user draft whole. Esc/outside-click
 * stays SAFE (S-2) because the draft survives the modal closing.
 *
 * FRONTDESK-REGISTER-PREFILL-TRUTH-V1 (2026-08-11 corrective): the draft is
 * now ALSO QUERY-bound (version 2). A draft captured under one search must
 * never resurface for a different (or absent) search -- that was the sticky
 * ghost-mobile defect: an abandoned registration's typed number silently
 * outliving its own search and overwriting every later search's honest
 * prefill. Esc/outside-click resume for the SAME search still works; a
 * mismatched draft is dead whole, same as a cross-user one.
 */

import type { CreateFormState } from '@/dashboard/patients/patient-master-view';
import {
  EMPTY_CREATE_FORM,
  isPlausibleMobile,
} from '@/dashboard/patients/patient-master-view';

// ---- Display language (FO-4 verbatim; everything else stays the existing
// pinned vocabulary) ---------------------------------------------------------

export const GLOBAL_SEARCH_NO_PATIENTS = 'No patients found';
export const GLOBAL_SEARCH_REGISTER_ACTION = 'Register patient';
export const GLOBAL_REGISTER_TITLE = 'Register patient';
export const GLOBAL_SEARCH_BACK_TO_SEARCH = 'Back to search';

// ---- The search-evidence gate (the in-modal C13 structural gate) -----------

/** Same three tokens as the standalone door's `from=` marker
 *  (patient-master-view.ts REGISTER_ORIGINS -- one vocabulary). */
export type GlobalRegisterEvidence = 'no-match' | 'none-of-these' | 'search-failed';

export interface GlobalSearchState {
  /** A search has RUN in this modal (not merely been typed). */
  readonly searched: boolean;
  /** Result count of the LAST completed search; null before any result. */
  readonly resultCount: number | null;
  /** The last search FAILED (service fault, not zero results). */
  readonly searchFailed: boolean;
  /** The operator pressed the explicit None-of-these under results. */
  readonly noneOfThese: boolean;
}

/**
 * The ONLY evidence states that open the in-modal register view; anything
 * else returns null and the door does not exist (C13: no creation door
 * before search evidence).
 */
export function globalRegisterEvidence(
  state: GlobalSearchState,
): GlobalRegisterEvidence | null {
  if (state.searchFailed) return 'search-failed';
  if (!state.searched) return null;
  if (state.resultCount === 0) return 'no-match';
  if (state.noneOfThese && (state.resultCount ?? 0) > 0) return 'none-of-these';
  return null;
}

/**
 * THE ONE query-derived register-field source (FRONTDESK-REGISTER-PREFILL-
 * TRUTH-V1). A plausible-mobile query fills `mobile`; anything else fills
 * `fullName`; an empty (trims-to-empty) query fills nothing -- a direct
 * register never inherits a phantom value. Callers apply this ON TOP OF a
 * freshly-reset form (never a spread of whatever a prior search or a prior
 * patient left in state), so the Mobile field always equals the query that
 * was actually searched, every open.
 */
export function registerFieldsFromQuery(query: string): Partial<CreateFormState> {
  const q = query.trim();
  if (q.length === 0) return {};
  return isPlausibleMobile(q) ? { mobile: q } : { fullName: q };
}

// ---- The D5-class register draft (session + actor + QUERY bound) -----------

export const GLOBAL_REGISTER_DRAFT_VERSION = 2;

export interface GlobalRegisterDraft {
  readonly version: number;
  /** The AUTHENTICATED STAFF identity this draft belongs to (the operator's
   *  own auth id -- never patient data). Wrong/missing actor = dead draft. */
  readonly actor: string;
  /** The exact search query (trimmed) this draft was captured under. A
   *  restore only ever applies when this matches the CURRENT search --
   *  otherwise the draft belongs to a different patient-context and is
   *  discarded whole (FRONTDESK-REGISTER-PREFILL-TRUTH-V1). */
  readonly query: string;
  readonly form: CreateFormState;
}

export function globalRegisterDraftKey(): string {
  return `ac.global-register-draft.v${GLOBAL_REGISTER_DRAFT_VERSION}`;
}

export function serializeGlobalRegisterDraft(draft: GlobalRegisterDraft): string {
  return JSON.stringify(draft);
}

/** STRICT parse (the parseArrivalDraft posture): any shape mismatch or an
 *  actor mismatch discards the WHOLE draft -- never half-restored. */
export function parseGlobalRegisterDraft(
  raw: string | null,
  expectedActor: string,
): GlobalRegisterDraft | null {
  if (raw === null || raw === '') return null;
  if (typeof expectedActor !== 'string' || expectedActor === '') return null;
  try {
    const v = JSON.parse(raw) as Partial<GlobalRegisterDraft>;
    if (v === null || typeof v !== 'object') return null;
    if (v.version !== GLOBAL_REGISTER_DRAFT_VERSION) return null;
    if (typeof v.actor !== 'string' || v.actor === '' || v.actor !== expectedActor) {
      return null;
    }
    if (typeof v.query !== 'string') return null;
    const f = v.form as Partial<CreateFormState> | undefined;
    if (f === null || typeof f !== 'object') return null;
    const out: Record<string, string> = {};
    for (const k of Object.keys(EMPTY_CREATE_FORM) as Array<keyof CreateFormState>) {
      const val = f[k];
      if (typeof val !== 'string') return null;
      out[k] = val;
    }
    return {
      version: GLOBAL_REGISTER_DRAFT_VERSION,
      actor: v.actor,
      query: v.query,
      form: out as unknown as CreateFormState,
    };
  } catch {
    return null;
  }
}
